import { NavLink, useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';
import { initials } from '../lib/format';
import SyncStatus from './SyncStatus';
import { useConfirm } from './ConfirmDialog';

const NAV = [
  { to: '/dashboard', label: 'Home',      ic: '▦' },
  { to: '/pipeline',  label: 'Pipeline',  ic: '▤' },
  { to: '/lead/new',  label: 'New',       ic: '＋' },
  { to: '/customers', label: 'Customers', ic: '☻' },
  { to: '/settings',  label: 'Settings',  ic: '⚙' },
];

// Sidebar only: the phone tab bar is already full, and the same screen is one
// tap away there from a project or customer page.
const SIDEBAR_EXTRA = [
  { to: '/documents', label: 'Documents', ic: '▣' },
  { to: '/email/new', label: 'Send email', ic: '✉' },
];

/** Company letterhead — the strip that makes the app feel like their paperwork. */
export function Letterhead() {
  const { settings } = useApp();
  const s = settings || {};
  return (
    <div className="letterhead">
      <img src="/logo.png" alt="" />
      <div>
        <div className="lh-name">{s.companyName || '5 River Roofing, LLC'}</div>
        {s.address ? <div className="lh-line">{s.address}</div> : null}
        {(s.phone || s.email) ? (
          <div className="lh-line">{[s.phone, s.email].filter(Boolean).join(' · ')}</div>
        ) : null}
      </div>
    </div>
  );
}

export function PageHeader({ title, sub, back, action }) {
  const navigate = useNavigate();
  return (
    <header className="page-header">
      <Letterhead />
      <div className="topbar">
        {back ? (
          <button className="back" onClick={() => navigate(back)} aria-label="Go back">‹</button>
        ) : null}
        <div className="titlewrap">
          <h1>{title}</h1>
          {sub ? <div className="sub">{sub}</div> : null}
        </div>
        {action}
      </div>
    </header>
  );
}

function Sidebar() {
  const { user, settings, signOut } = useApp();
  return (
    <nav className="sidebar" aria-label="Main">
      <div className="sidebar-brand">
        <img src="/logo.png" alt="" />
        <div>
          <div className="nm">{settings?.companyName || '5 River Roofing'}</div>
          <div className="sb">{user?.name}</div>
        </div>
      </div>
      {[...NAV.slice(0, -1), ...SIDEBAR_EXTRA, NAV[NAV.length - 1]].map(n => (
        <NavLink key={n.to} to={n.to} className="side-link">
          <span className="ic" aria-hidden="true">{n.ic}</span>
          {n.label}
        </NavLink>
      ))}
      <div className="sidebar-foot">
        <button className="side-link" style={{ width: '100%' }} onClick={signOut}>
          <span className="ic" aria-hidden="true">⏻</span>
          Sign out
        </button>
      </div>
    </nav>
  );
}

function TabBar() {
  return (
    <nav className="tabbar" aria-label="Main">
      {NAV.map(n => (
        <NavLink key={n.to} to={n.to} className="tab">
          <span className="ic" aria-hidden="true">{n.ic}</span>
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * App shell. The same navigation items render as a bottom tab bar on a phone
 * and a persistent sidebar from 1024px up — chosen in CSS so there's no
 * resize listener and no flash of the wrong layout on first paint.
 */
export function Layout({ children }) {
  return (
    <div className="shell">
      <a className="skip-link" href="#main">Skip to content</a>
      <Sidebar />
      <div className="shell-main">
        <SyncStatus />
        <main id="main">{children}</main>
      </div>
      <TabBar />
    </div>
  );
}

export const Avatar = () => {
  const { user, signOut } = useApp();
  const [ask, confirmDialog] = useConfirm();
  return (
    <>
      <button
        className="avatar"
        onClick={async () => {
          if (await ask({ title: 'Sign out?', confirmLabel: 'Sign out', danger: false, icon: '⏻' })) signOut();
        }}
        aria-label={`Signed in as ${user?.name || 'user'}. Sign out`}
      >
        {initials(user?.name || '?')}
      </button>
      {confirmDialog}
    </>
  );
};
